import type { ReactNode } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import {
    FLOW_GROUPS,
    hasFlow,
    type FlowGroup,
    type FlowGroupId,
    type LatestFlow,
} from '../../flow/groups';
import { formatNetCapital, formatWallets } from '../../flow/summary';
import type { Outcome } from '../../model/types';
import { formatVolume } from '../../../utils/format';
import { LiveProbability } from '../LiveProbability';
import { isClosed, volumeOf, type OutcomeSort } from '../ranking';
import { OutcomePortrait } from './OutcomePortrait';
import {
    barFraction,
    flowOf,
    probabilityText,
    shareText,
    volumeText,
} from './outcomeMeasure';

// The tags' tints are alpha tints and work on both themes; the text needs a
// darker orange/blue on the light card and a lighter one on the dark card.
const TAGS = {
    light: {
        informed: { bg: 'rgba(239, 148, 0, 0.14)', fg: '#8a5300' },
        whales: { bg: 'rgba(47, 104, 172, 0.12)', fg: '#234f84' },
    },
    dark: {
        informed: { bg: 'rgba(245, 158, 11, 0.2)', fg: '#ffc66b' },
        whales: { bg: 'rgba(59, 130, 246, 0.22)', fg: '#9cc4ff' },
    },
} as const;

const mutedText = (theme: Theme) => alpha(theme.palette.text.primary, 0.55);

interface PhoneOutcomeRowProps {
    outcome: Outcome;
    color: string | null;
    selected: boolean;
    clickable: boolean;
    /** What the switch is on: picks the figure and the bar. */
    measure: OutcomeSort;
    totalVolume: number;
    flow: ReadonlyMap<FlowGroupId, LatestFlow> | undefined;
    /** The biggest leading net capital per group (Flow's bar scales). */
    largestFlow: ReadonlyMap<FlowGroupId, number>;
    onToggle: () => void;
    /** For the list, which moves focus to a row it has just revealed. */
    rowRef?: (element: HTMLElement | null) => void;
}

const ROW_SX = {
    display: 'grid',
    gridTemplateColumns: '36px minmax(0, 1fr) auto',
    columnGap: '12px',
    rowGap: '8px',
    alignItems: 'center',
    justifyContent: 'stretch',
    width: '100%',
    boxSizing: 'border-box',
    padding: '12px 16px',
    border: 0,
    borderTop: '1px solid',
    borderTopColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08),
    borderRadius: 0,
    font: 'inherit',
    color: 'inherit',
    textAlign: 'left',
} as const;

// Keyboard focus: the selected tint alone is too faint to find the row by.
// Inside the box (negative offset), the rows run edge to edge in the card.
export const FOCUS_RING = {
    outline: '2px solid',
    outlineColor: (theme: Theme) => theme.palette.primary.main,
    outlineOffset: '-2px',
} as const;

const FIGURE_SX = {
    fontSize: '17px',
    fontWeight: 700,
    lineHeight: 'normal',
    // LiveProbability is a Typography, which brings its own tracking
    letterSpacing: 'normal',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
} as const;

/** The main figure on the right: what the switch is on. */
function Figure({
    outcome,
    measure,
    totalVolume,
    flow,
}: Pick<PhoneOutcomeRowProps, 'outcome' | 'measure' | 'totalVolume' | 'flow'>) {
    let content: ReactNode;
    if (measure === 'probability') {
        content = <LiveProbability outcome={outcome} sx={FIGURE_SX} />;
    } else if (measure === 'volume') {
        content = (
            <Box component="span" sx={FIGURE_SX}>
                {formatVolume(volumeOf(outcome))}{' '}
                <Box
                    component="span"
                    sx={{
                        fontSize: '12px',
                        fontWeight: 400,
                        color: mutedText,
                    }}
                >
                    ({shareText(outcome, totalVolume)})
                </Box>
            </Box>
        );
    } else {
        const picked = flowOf(flow);
        content = (
            <Box component="span" sx={FIGURE_SX}>
                {picked ? formatNetCapital(picked.value) : '–'}
            </Box>
        );
    }
    return (
        <Box
            component="span"
            data-testid={`outcome-figure-${outcome.id}`}
            sx={{ display: 'block' }}
        >
            {content}
        </Box>
    );
}

/**
 * Which group a Flow figure is (Informed or Whales): a dot in the group's
 * colour and the word, under the figure. The bar is in the same colour.
 */
function FlowGroupLabel({
    outcomeId,
    group,
}: {
    outcomeId: string;
    group: FlowGroup;
}) {
    return (
        <Box
            component="span"
            data-testid={`outcome-group-${outcomeId}`}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                lineHeight: 'normal',
                color: mutedText,
            }}
        >
            <Box
                component="span"
                aria-hidden="true"
                sx={{
                    width: 8,
                    height: 8,
                    flexShrink: 0,
                    borderRadius: '50%',
                    bgcolor: group.color,
                }}
            />
            {group.label}
        </Box>
    );
}

export function PhoneOutcomeRow({
    outcome,
    color,
    selected,
    clickable,
    measure,
    totalVolume,
    flow,
    largestFlow,
    onToggle,
    rowRef,
}: PhoneOutcomeRowProps) {
    const closed = isClosed(outcome);
    const picked = measure === 'flow' ? flowOf(flow) : null;
    const fraction = barFraction(measure, outcome, {
        totalVolume,
        largestFlow: picked ? largestFlow.get(picked.group.id) ?? 0 : 0,
        flow: picked,
    });
    const sub = [
        closed ? 'Closed' : null,
        measure === 'volume'
            ? `Prob ${probabilityText(outcome)}`
            : `Vol ${volumeText(outcome, totalVolume)}`,
    ]
        .filter(Boolean)
        .join(' · ');
    const tags = FLOW_GROUPS.flatMap(group => {
        const value = flow?.get(group.id);
        return value && hasFlow(value) ? [{ group, value }] : [];
    });

    const content = (
        <>
            <OutcomePortrait
                outcomeId={outcome.id}
                label={outcome.label}
                image={outcome.image}
                color={color}
            />
            <Box
                component="span"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    minWidth: 0,
                }}
            >
                <Box
                    component="span"
                    data-testid={`outcome-name-${outcome.id}`}
                    sx={{
                        fontSize: '15px',
                        fontWeight: selected ? 700 : 500,
                        lineHeight: 1.3,
                        // up to two lines, then an ellipsis; a long name
                        // without a space still breaks instead of widening
                        // the row
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {outcome.label}
                </Box>
                <Box
                    component="span"
                    data-testid={`outcome-sub-${outcome.id}`}
                    sx={{
                        fontSize: '12px',
                        lineHeight: 'normal',
                        color: mutedText,
                    }}
                >
                    {sub}
                </Box>
            </Box>
            <Box
                component="span"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: '2px',
                }}
            >
                <Figure
                    outcome={outcome}
                    measure={measure}
                    totalVolume={totalVolume}
                    flow={flow}
                />
                {picked && (
                    <FlowGroupLabel
                        outcomeId={outcome.id}
                        group={picked.group}
                    />
                )}
            </Box>
            <Box
                component="span"
                sx={{
                    gridColumn: '2 / 4',
                    display: 'block',
                    height: 6,
                    borderRadius: '3px',
                    overflow: 'hidden',
                    bgcolor: t => alpha(t.palette.text.primary, 0.08),
                }}
            >
                <Box
                    component="span"
                    data-testid={`outcome-bar-${outcome.id}`}
                    style={{ width: `${Math.round(fraction * 10000) / 100}%` }}
                    sx={{
                        display: 'block',
                        height: 6,
                        // on Flow the bar is the group's colour, not the outcome's
                        bgcolor:
                            picked?.group.color ?? color ?? 'text.disabled',
                    }}
                />
            </Box>
            {tags.length > 0 && (
                <Box
                    component="span"
                    data-testid={`outcome-tags-${outcome.id}`}
                    sx={{
                        gridColumn: '2 / 4',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                    }}
                >
                    {tags.map(({ group, value }) => (
                        <Box
                            key={group.id}
                            component="span"
                            sx={{
                                fontSize: '12px',
                                lineHeight: 'normal',
                                padding: '3px 8px',
                                borderRadius: '12px',
                                bgcolor: t => TAGS[t.palette.mode][group.id].bg,
                                color: t => TAGS[t.palette.mode][group.id].fg,
                            }}
                        >
                            {`${group.label} ${formatNetCapital(value)} · ${formatWallets(value.wallets)}`}
                        </Box>
                    ))}
                </Box>
            )}
        </>
    );

    const tint = (t: Theme) =>
        selected ? alpha(t.palette.primary.main, 0.1) : 'transparent';

    if (!clickable) {
        return (
            <Box
                ref={rowRef}
                // not a tab stop, but the list can move focus here
                tabIndex={-1}
                data-testid={`outcome-row-${outcome.id}`}
                sx={{ ...ROW_SX, bgcolor: tint, '&:focus-visible': FOCUS_RING }}
            >
                {content}
            </Box>
        );
    }
    return (
        <ButtonBase
            ref={rowRef}
            data-testid={`outcome-row-${outcome.id}`}
            aria-pressed={selected}
            onClick={onToggle}
            sx={{
                ...ROW_SX,
                bgcolor: tint,
                '&.Mui-focusVisible': {
                    ...FOCUS_RING,
                    bgcolor: t => alpha(t.palette.primary.main, 0.16),
                },
                '@media (hover: hover)': {
                    '&:hover': {
                        bgcolor: t =>
                            selected
                                ? alpha(t.palette.primary.main, 0.14)
                                : t.palette.action.hover,
                    },
                },
            }}
        >
            {content}
        </ButtonBase>
    );
}
